import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiDroplet, FiActivity, FiInfo, FiSliders, FiTrendingUp } from 'react-icons/fi';
import classNames from 'classnames';
import { Chemistry, ChlorineSettings, MineralsChlorinatorInfo, MineralsOzoneInfo, PhSettings } from '../../../types/chemistry';
import { AuxTypeCode } from '../../../types/device';
import { Aux ,Dashboard,AuxAssign} from '../../../types/dashboard';
import ChemistryCard from '../../../components/dashboard/ChemistryCard';
import { apiService } from '@/utils/api';
import { useAppSelector } from '@/store';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { setLoading } from '@/store/slices/appSlice';
import { useDispatch } from 'react-redux';
import { getRs485ChlorinatorWarnings, getRs485OzoneWarnings } from '@/utils/rs485Warnings';

interface ChemistrySidebarProps {
  dashboardData?: Dashboard;
  auxDevices?: Aux[];
  chemistryData?: {
    chemistry: Chemistry;
    chlorineSettings: ChlorineSettings;
    phSettings: PhSettings;
    auxAssign?: AuxAssign;
  };
}

// 数据行组件
const DataRow: React.FC<{
  label: string;
  value: React.ReactNode;
  isActive?: boolean;
  unit?: string;
}> = ({ label, value, isActive = true, unit }) => (
  <div className="flex justify-between items-center py-1.5">
    <span className={classNames("text-sm", {
      "text-gray-600 dark:text-gray-300": isActive,
      "text-gray-400 dark:text-gray-500": !isActive
    })}>
      {label}
    </span>
    <span className={classNames("font-semibold", {
      "text-gray-800 dark:text-gray-100": isActive,
      "text-gray-400 dark:text-gray-500": !isActive
    })}>
      {value}{unit ? ` ${unit}` : ''}
    </span>
  </div>
);

// 卡片头部组件
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  isActive?: boolean;
}> = ({ title, icon, isActive = true }) => (
  <h3 className={classNames(
    "flex items-center font-medium mb-4",
    {
      "text-gray-800 dark:text-gray-100": isActive,
      "text-gray-400 dark:text-gray-500": !isActive
    }
  )}>
    <span className={classNames("mr-2", {
      "text-blue-500 dark:text-blue-400": isActive,
      "text-gray-400 dark:text-gray-500": !isActive
    })}>
      {icon}
    </span>
    {title}
  </h3>
);

// 信息框组件
const InfoBox: React.FC<{
  text: string;
  isActive?: boolean;
}> = ({ text, isActive = true }) => (
  <div className={classNames(
    "mt-4 p-3 rounded text-sm",
    {
      "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300": isActive,
      "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300": !isActive
    }
  )}>
    <div className="flex items-start">
      <FiInfo className="mt-0.5 mr-2 flex-shrink-0" />
      <p>{text}</p>
    </div>
  </div>
);

const ChemistrySidebar: React.FC<ChemistrySidebarProps> = ({ 
  dashboardData,
  auxDevices = [], 
  chemistryData 
}) => {
  const selectedDeviceId = useAppSelector((s) => s.device.selectedDeviceId);
  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmOkText, setConfirmOkText] = useState('Confirm');
  const [confirmCancelText, setConfirmCancelText] = useState('Cancel');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmContent, setConfirmContent] = useState<React.ReactNode>('');
  const confirmActionRef = useRef<null | (() => Promise<void>)>(null);
  const [mineralsChlorinatorInfo, setMineralsChlorinatorInfo] = useState<MineralsChlorinatorInfo | null>(null);
  const [mineralsOzoneInfo, setMineralsOzoneInfo] = useState<MineralsOzoneInfo | null>(null);

  const dispatch = useDispatch();
  // Helper function to format duration
  const formatDuration = useMemo(() => (minutes?: number) => {
    if (minutes === undefined) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }, []);
  // 检测设备类型
  // 添加调试日志
  
  const hasPHDoser = chemistryData?.auxAssign?.equipment.some(device => device.type_code === AuxTypeCode.PH_DOSER) || false;
  const hasSaltChlorinator = chemistryData?.auxAssign?.equipment.some(device => 
    device.type_code === AuxTypeCode.SALT_CHLORINATOR || 
    device.type_code === AuxTypeCode.MINERAL_CHLORINATOR
  ) || false;
  const hasLiquidChlorine = (
    chemistryData?.auxAssign?.equipment.some(device => device.type_code === AuxTypeCode.LIQUID_CHLORINE) ||
    auxDevices.some(device => device.aux_type_code === AuxTypeCode.MINERAL_CHLORINATOR)
  ) || false;
  const hasNakedIoniser = chemistryData?.auxAssign?.equipment.some(device => device.type_code === AuxTypeCode.NAKED_IONISER) || false;
  const hasRS485 = chemistryData?.auxAssign?.has_ecoclear || chemistryData?.auxAssign?.equipment.some(device => device.type_code === AuxTypeCode.RS485) || false;
  
  // 打印检测结果
  console.log('ChemistrySidebar - 设备检测结果:', {
    hasPHDoser,
    hasSaltChlorinator,
    hasLiquidChlorine,
    hasNakedIoniser,
    hasRS485,
    'AuxTypeCode.PH_DOSER': AuxTypeCode.PH_DOSER
  });


  // 判断各部分是否激活
  const isPHDoserActive = chemistryData?.phSettings?.ph_dosing;
  const isLiquidDoserActive = chemistryData?.chlorineSettings?.liquid_chlorine_dosing ;
  const isMineralDoserActive = chemistryData?.chlorineSettings?.orp_controlled;

  useEffect(() => {
    if (!selectedDeviceId || !hasRS485) {
      setMineralsChlorinatorInfo(null);
      setMineralsOzoneInfo(null);
      return;
    }

    let cancelled = false;

    const fetchMineralsInfo = async () => {
      try {
        const [chlorinatorResp, ozoneResp] = await Promise.all([
          apiService.get<{ mineralsInfo: MineralsChlorinatorInfo }>(
            `/api/v1/iot-shadow/device/${selectedDeviceId}/mineralsInfo?type=1`
          ),
          apiService.get<{ mineralsInfo: MineralsOzoneInfo }>(
            `/api/v1/iot-shadow/device/${selectedDeviceId}/mineralsInfo?type=2`
          ),
        ]);

        if (cancelled) return;

        setMineralsChlorinatorInfo(chlorinatorResp.mineralsInfo || null);
        setMineralsOzoneInfo(ozoneResp.mineralsInfo || null);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch RS485 minerals info:', error);
      }
    };

    fetchMineralsInfo();

    return () => {
      cancelled = true;
    };
  }, [hasRS485, selectedDeviceId]);

  // Next dosing calculation and text (Android parity)
  const getNextDosingText = (
    timer?: number,
    dosingTime?: number,
    intervals?: number,
    enabled?: boolean
  ): { text: string; isGreen: boolean } => {
    const t = (intervals ?? 0) * 60 + (dosingTime ?? 0);
    const current = timer ?? 0;
    const isDoing = current < (dosingTime ?? 0) && !!enabled;
    const isWaitingNext = ((current >= (dosingTime ?? 0)) && (current <= t)) || !enabled;
    if (isDoing) {
      return { text: 'Doser is dosing right now!', isGreen: false };
    }
    if (isWaitingNext) {
      const remaining = t - current;
      if (remaining > 0) {
        if (remaining >= 60) {
          const m = Math.floor(remaining / 60);
          const s = remaining % 60;
          return { text: s > 0 ? `Next dosing is in ${m} minutes ${s} seconds` : `Next dosing is in ${m} minutes`, isGreen: true };
        }
        return { text: `Next dosing is in ${remaining} seconds`, isGreen: true };
      }
      return { text: 'Next dosing is ready', isGreen: true };
    }
    return { text: '', isGreen: false };
  };

  const formatLiters = (v?: number | null) => (v != null ? `${(v / 1000).toFixed(1)} Ltrs` : '-');
  const calcProgressPct = (remain?: number | null, drum?: number | null) => {
    const r = remain ?? 0;
    const d = drum ?? 0;
    if (!d) return 100;
    const pct = (r / d) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  };

  // Reset handlers (reference DeviceSettingsPage)
  const handleResetPh = async () => {
    const ph = chemistryData?.phSettings;
    if (!selectedDeviceId || !ph) return;
   
    const payload = {
      ph_dosing: ph.ph_dosing ?? false,
      ph_dosing_time: ph.ph_dosing_time ?? 0,
      desired_ph_level: ph.desired_ph_level ?? 0,
      acid_drum_volume: ph.acid_drum_volume ?? 0,
      remain_acid_volume: ph.acid_drum_volume ?? 0,
      reset_acid_volume: true,
      max_dose_pre_day: ph.max_dose_pre_day ?? 0,
      dose_intervals: ph.dose_intervals ?? 0,
      ph_dose_rate: ph.ph_dose_rate ?? 0,
    };
    setConfirmTitle('Reset Acid Volume');
    setConfirmOkText('Reset');
    setConfirmCancelText('Cancel');
    setConfirmContent(<p>Reset Acid Volume to drum volume?</p>);
    confirmActionRef.current = async () => {
      dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setPhSettings`, payload);
      } catch (err) {
        console.error('Reset Acid Volume failed', err);
      } finally {
        dispatch(setLoading(false));
      }
    };
    setConfirmOpen(true);
  };

  const handleResetChlorine = async () => {
    const chl = chemistryData?.chlorineSettings;
    if (!selectedDeviceId || !chl) return;
    const payload = {
      orp_controlled: chl.orp_controlled ?? false,
      desired_orp_level_mineral: chl.desired_orp_level_mineral ?? 0,
      liquid_chlorine_dosing: chl.liquid_chlorine_dosing ?? false,
      desired_orp_level_liquid: chl.desired_orp_level_liquid ?? 0,
      chlorine_dosing_time: chl.chlorine_dosing_time ?? 0,
      chlorine_drum_volume: chl.chlorine_drum_volume ?? 0,
      remain_chlorine_volume: chl.chlorine_drum_volume ?? 0,
      reset_chlorine_volume: true,
      max_dose_pre_day: chl.max_dose_pre_day ?? 0,
      dose_intervals: chl.dose_intervals ?? 0,
      liquid_dose_rate: chl.liquid_dose_rate ?? 0,
    };
    setConfirmTitle('Reset Chlorine Volume');
    setConfirmOkText('Reset');
    setConfirmCancelText('Cancel');
    setConfirmContent(<p>Reset Chlorine Volume to drum volume?</p>);
    confirmActionRef.current = async () => {
       dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setChlorineSettings`, payload);
      } catch (err) {
        console.error('Reset Chlorine Volume failed', err);
      } finally {
        dispatch(setLoading(false));
      }
    };
    setConfirmOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Chemistry Overview */}
      <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
        {dashboardData && (
          <ChemistryCard 
            dashboardData={{
              dashboard: {
                chemistry_stable: dashboardData.chemistry_stable,
                actual_ph: dashboardData.actual_ph ?? 70,
                actual_orp: dashboardData.actual_orp,
                d_ph: dashboardData.d_ph / 10 ,
                d_orp_mineral: hasLiquidChlorine 
                  ? dashboardData.d_orp_liquid
                  : hasSaltChlorinator 
                    ? dashboardData.d_orp_mineral
                    : 700,
                main_pump_status: 1, // Assuming pump is on when viewing sidebar
                is_naked: hasNakedIoniser
              }
            }}
            showSidebarToggle={false}
          />
        )}
      </div>

      {/* PH Doser */}
      {hasPHDoser && (
        <div className={classNames(
          "p-4 rounded-lg border",
          {
            "bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700": isPHDoserActive,
            "bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700": !isPHDoserActive
          }
        )}>
          <div className="flex items-center justify-between mb-2">
            <SectionHeader 
              title="pH Doser" 
              icon={<FiDroplet size={18} />}
              isActive={isPHDoserActive}
            />
            <button
              type="button"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
              onClick={handleResetPh}
            >
              RESET
            </button>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Remaining Chemicals</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{formatLiters(chemistryData?.phSettings?.remain_acid_volume)}</span>
            </div>
            <div>
              <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded">
                <div
                  className="h-1 bg-teal-500 rounded"
                  style={{ width: `${calcProgressPct(chemistryData?.phSettings?.remain_acid_volume, chemistryData?.phSettings?.acid_drum_volume)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>0 Ltrs</span>
                <span>{formatLiters(chemistryData?.phSettings?.acid_drum_volume)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Doses per day</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{chemistryData?.phSettings?.current_dose_today ?? 0} / {chemistryData?.phSettings?.max_dose_pre_day ?? 0}</span>
            </div>
            {(() => {
              const { text, isGreen } = getNextDosingText(
                chemistryData?.phSettings?.ph_doser_timer,
                chemistryData?.phSettings?.ph_dosing_time,
                chemistryData?.phSettings?.dose_intervals,
                dashboardData?.ph_switch_status
              );
              return text ? (
                <div className={classNames("text-sm", isGreen ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400")}> 
                  {text}
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Liquid Chlorine Doser */}
      {hasLiquidChlorine && (
        <div className={classNames(
          "p-4 rounded-lg border",
          {
            "bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700": isLiquidDoserActive,
            "bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700": !isLiquidDoserActive
          }
        )}>
          <div className="flex items-center justify-between mb-2">
            <SectionHeader 
              title="Liquid Chlorinator" 
              icon={<FiSliders size={18} />}
              isActive={isLiquidDoserActive}
            />
            <button
              type="button"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
              onClick={handleResetChlorine}
            >
              RESET
            </button>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Remaining Chemicals</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{formatLiters(chemistryData?.chlorineSettings?.remain_chlorine_volume)}</span>
            </div>
            <div>
              <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded">
                <div
                  className="h-1 bg-teal-500 rounded"
                  style={{ width: `${calcProgressPct(chemistryData?.chlorineSettings?.remain_chlorine_volume, chemistryData?.chlorineSettings?.chlorine_drum_volume)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>0 Ltrs</span>
                <span>{formatLiters(chemistryData?.chlorineSettings?.chlorine_drum_volume)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Doses per day</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{chemistryData?.chlorineSettings?.current_dose_today ?? 0} / {chemistryData?.chlorineSettings?.max_dose_pre_day ?? 0}</span>
            </div>
            {(() => {
              const { text, isGreen } = getNextDosingText(
                chemistryData?.chlorineSettings?.liquid_doser_timer,
                chemistryData?.chlorineSettings?.chlorine_dosing_time,
                chemistryData?.chlorineSettings?.dose_intervals,
                dashboardData?.orp_switch_status
              );
              return text ? (
                <div className={classNames("text-sm", isGreen ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400")}> 
                  {text}
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Mineral Doser */}
      {hasSaltChlorinator && (
        <div className={classNames(
          "p-4 rounded-lg border",
          {
            "bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700": isMineralDoserActive,
            "bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700": !isMineralDoserActive
          }
        )}>
          <SectionHeader 
            title="Mineral Doser" 
            icon={<FiTrendingUp size={18} />}
            isActive={isMineralDoserActive}
          />
          
          <div className="space-y-1">
            <DataRow 
              label="Status" 
              value={isMineralDoserActive ? "Enabled" : "Disabled"} 
              isActive={isMineralDoserActive}
            />
            <DataRow 
              label="Target ORP Level" 
              value={chemistryData?.chlorineSettings?.desired_orp_level_mineral ?? 'N/A'} 
              isActive={isMineralDoserActive}
              unit="mV" 
            />
            <DataRow 
              label="Dosing Duration Today" 
              value={formatDuration(chemistryData?.chlorineSettings?.mineral_dosing_duration_today)} 
              isActive={isMineralDoserActive}
            />
            <DataRow 
              label="Max Duration Per Day" 
              value={formatDuration(chemistryData?.chlorineSettings?.mineral_max_duration_preday)} 
              isActive={isMineralDoserActive}
            />
          </div>

          <InfoBox 
            text="Mineral Doser manages mineral/salt levels in the pool to maintain proper sanitization."
            isActive={isMineralDoserActive}
          />
        </div>
      )}

      {hasRS485 && (
        <div className="space-y-4">
          {mineralsChlorinatorInfo?.chlo_detected ? (
            <div className="p-4 rounded-lg border bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700">
              <SectionHeader
                title="RS485 Chlorinator"
                icon={<FiTrendingUp size={18} />}
                isActive={((mineralsChlorinatorInfo.status_bitlow ?? 0) & 1) === 1}
              />
              <div className="space-y-1">
                <DataRow label="Brand" value="Mineral Swim V2" />
                <DataRow label="Average Current" value={mineralsChlorinatorInfo.average_current ?? 0} unit="mA" />
                <DataRow label="Read Setpoint" value={mineralsChlorinatorInfo.chlorinator_read_setpoint ?? 0} unit="%" />
              </div>
              {!chemistryData?.chlorineSettings?.orp_controlled && (
                <InfoBox
                  text="ORP Controlled is disabled. RS485 chlorinator output will not be automatically controlled."
                  isActive={false}
                />
              )}
              {((mineralsChlorinatorInfo.status_bitlow ?? 0) & 1) === 1 &&
                getRs485ChlorinatorWarnings(mineralsChlorinatorInfo.warning_bl ?? 0).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {getRs485ChlorinatorWarnings(mineralsChlorinatorInfo.warning_bl ?? 0).map((warning) => (
                      <div key={warning} className="text-sm font-medium text-rose-600 dark:text-rose-400">
                        {warning}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ) : (
            <div className="p-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              <SectionHeader title="RS485 Chlorinator" icon={<FiTrendingUp size={18} />} isActive={false} />
              <div className="text-sm text-gray-500 dark:text-gray-400">RS485 chlorinator is detached.</div>
            </div>
          )}

          {mineralsOzoneInfo?.ozone_detected ? (
            <div className="p-4 rounded-lg border bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700">
              <SectionHeader
                title="RS485 Ozone"
                icon={<FiActivity size={18} />}
                isActive={((mineralsOzoneInfo.status_bitlow ?? 0) & 1) === 1}
              />
              <div className="space-y-1">
                <DataRow label="Brand" value="Mineral Swim V2" />
                <DataRow label="Current" value={mineralsOzoneInfo.current ?? 0} unit="mA" />
                <DataRow label="Read Setpoint" value={mineralsOzoneInfo.ozone_read_setpoint ?? 0} unit="%" />
              </div>
              {((mineralsOzoneInfo.status_bitlow ?? 0) & 1) === 1 &&
                getRs485OzoneWarnings(mineralsOzoneInfo.warning_bl ?? 0).length > 0 && (
                  <div className="mt-4 space-y-2">
                    {getRs485OzoneWarnings(mineralsOzoneInfo.warning_bl ?? 0).map((warning) => (
                      <div key={warning} className="text-sm font-medium text-rose-600 dark:text-rose-400">
                        {warning}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ) : (
            <div className="p-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              <SectionHeader title="RS485 Ozone" icon={<FiActivity size={18} />} isActive={false} />
              <div className="text-sm text-gray-500 dark:text-gray-400">RS485 ozone is detached.</div>
            </div>
          )}
        </div>
      )}

      {/* Naked Ioniser */}
      {hasNakedIoniser && (
        <div className="p-4 rounded-lg border bg-white border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <SectionHeader 
            title="Naked Ioniser" 
            icon={<FiActivity size={18} />}
            isActive={true}
          />
          
          <div className="space-y-1">
            <DataRow 
              label="Status" 
              value="Enabled" 
              isActive={true}
            />
          </div>

          <InfoBox 
            text="Naked Ioniser uses copper and silver ions to sanitize pool water without traditional chemicals."
            isActive={true}
          />
        </div>
      )}
      {/* Confirm dialog for reset actions */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        onCancel={() => setConfirmOpen(false)}
        onOk={async () => {
          if (!confirmActionRef.current) {
            setConfirmOpen(false);
            return;
          }
          try {
            setConfirmLoading(true);
            await confirmActionRef.current();
          } finally {
            setConfirmLoading(false);
            setConfirmOpen(false);
          }
        }}
        cancelText={confirmCancelText}
        okText={confirmOkText}
        loading={confirmLoading}
        fullWidth
        maxWidth="sm"
      >
        {confirmContent}
      </ConfirmDialog>
    </div>
  );
};

export default ChemistrySidebar;
